import { Role } from '@/routes/PrivateRoute';
import useAuth from '@/hooks/useAuth';

// V2 Order Flow is enabled for all tenant admins, plus customer admins whose
// client was created on or after the cutover. Clients onboarded before the
// cutover stay on V1 unless explicitly allowlisted below.
//
// The cutover is compared against `customer.createdAt` (UTC), which the
// CurrentCustomerAdmin fragment selects on login.
//
// 2026-06-01: "clients created from June 2026 onward get V2" — the boundary is
// the START of June, so June's own clients are included.
export const ORDER_FLOW_V2_CUTOVER = Date.parse('2026-06-01T00:00:00Z');

// Pre-cutover clients granted V2 access early. Add a customer id to expand
// per-client access; clients created after the cutover need no entry here.
const ORDER_FLOW_V2_ALLOWED_CUSTOMER_IDS = new Set([
  '836', // Avila Golf & Country Club
  // PROD-1545: clients posting from templates
  '9', // 12 Stories
  '153', // The Darcy
  '171', // Trinity Metz
  '420', // Navy Yard Guest Services
]);

/**
 * Whether a client (customer) gets the V2 order flow: allowlisted, or created
 * on/after the cutover. Exported so it can be unit tested without a hook.
 */
export const isOrderFlowV2Customer = (customer: {
  id: string;
  createdAt: string;
}): boolean => {
  if (ORDER_FLOW_V2_ALLOWED_CUSTOMER_IDS.has(customer.id)) return true;

  const createdAt = Date.parse(customer.createdAt);
  // An unparseable timestamp must not silently grant access.
  if (Number.isNaN(createdAt)) return false;

  return createdAt >= ORDER_FLOW_V2_CUTOVER;
};

const useOrderFlowV2Enabled = (): boolean => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  if (!currentAdmin) return false;
  // Tenant admins always get V2.
  if (currentAdmin.role === Role.TENANT_ADMIN) return true;
  // Customer admins: allowlisted, or created on/after the cutover.
  if (isCurrentCustomerAdmin(currentAdmin)) {
    return isOrderFlowV2Customer(currentAdmin.customer);
  }
  return false;
};

export default useOrderFlowV2Enabled;
